import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { LOGO } from "@/lib/brand";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Mic2, Building2, Disc3, DollarSign, LifeBuoy,
  FolderOpen, Settings, Sun, Moon, LogOut, Menu, PlusCircle,
  FileSignature, ShieldAlert, History, ClipboardCheck, Wallet, UploadCloud, SlidersHorizontal, Network,
} from "lucide-react";

const NAV = {
  super_admin: [
    ["/", "Dashboard", LayoutDashboard],
    ["/approval-queue", "Approval Queue", ClipboardCheck],
    ["/users", "Users", Users],
    ["/manager-mapping", "Manager Mapping", Network],
    ["/artists", "Artists", Mic2],
    ["/labels", "Labels", Building2],
    ["/releases", "Releases", Disc3],
    ["/contracts", "Contracts", FileSignature],
    ["/takedowns", "Takedowns", ShieldAlert],
    ["/royalties", "Royalties", DollarSign],
    ["/withdrawals", "Withdrawals", Wallet],
    ["/bulk-upload", "Bulk Upload", UploadCloud],
    ["/tickets", "Tickets", LifeBuoy],
    ["/files", "Files", FolderOpen],
    ["/custom-fields", "Custom Fields", SlidersHorizontal],
    ["/audit", "Audit Log", History],
    ["/settings", "Settings", Settings],
  ],
  operations: [
    ["/", "Dashboard", LayoutDashboard],
    ["/approval-queue", "Approval Queue", ClipboardCheck],
    ["/releases", "Releases", Disc3],
    ["/takedowns", "Takedowns", ShieldAlert],
    ["/bulk-upload", "Bulk Upload", UploadCloud],
    ["/files", "Files", FolderOpen],
    ["/tickets", "Tickets", LifeBuoy],
    ["/settings", "Settings", Settings],
  ],
  accounts: [
    ["/", "Dashboard", LayoutDashboard],
    ["/approval-queue", "Approval Queue", ClipboardCheck],
    ["/royalties", "Royalties", DollarSign],
    ["/tickets", "Tickets", LifeBuoy],
    ["/settings", "Settings", Settings],
  ],
  label_manager: [
    ["/", "Dashboard", LayoutDashboard],
    ["/approval-queue", "Approval Queue", ClipboardCheck],
    ["/managed", "My Roster", FolderOpen],
    ["/artists", "Artists", Mic2],
    ["/labels", "Labels", Building2],
    ["/releases", "Releases", Disc3],
    ["/royalties", "Royalties", DollarSign],
    ["/tickets", "Tickets", LifeBuoy],
    ["/settings", "Settings", Settings],
  ],
  artist: [
    ["/", "Dashboard", LayoutDashboard],
    ["/releases", "My Releases", Disc3],
    ["/releases/new", "Submit Release", PlusCircle],
    ["/takedowns", "Takedowns", ShieldAlert],
    ["/contracts", "Contracts", FileSignature],
    ["/royalties", "Royalties", DollarSign],
    ["/withdrawals", "Withdrawals", Wallet],
    ["/tickets", "Support Tickets", LifeBuoy],
    ["/settings", "Profile", Settings],
  ],
  label: [
    ["/", "Dashboard", LayoutDashboard],
    ["/artists", "Artists", Mic2],
    ["/releases", "Releases", Disc3],
    ["/takedowns", "Takedowns", ShieldAlert],
    ["/contracts", "Contracts", FileSignature],
    ["/royalties", "Royalties", DollarSign],
    ["/withdrawals", "Withdrawals", Wallet],
    ["/tickets", "Support Tickets", LifeBuoy],
    ["/settings", "Settings", Settings],
  ],
};

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("aru_theme") || "dark");
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("aru_theme", theme);
  }, [theme]);
  return [theme, setTheme];
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const links = (NAV[user.role] || NAV.artist).filter(([to]) => !(to === "/withdrawals" && user.role === "artist" && user.label_id));

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-card/60 backdrop-blur-xl transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center justify-center border-b border-border px-4">
          <img src={LOGO} alt="ARU Music Records" className="h-11 w-auto object-contain" data-testid="sidebar-logo" />
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {links.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-border p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Logged in as</p>
          <p className="truncate text-sm font-semibold">{user.name}</p>
          <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
            {ROLE_LABELS[user.role]}
          </span>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-md lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-7xl space-y-8 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
